import { useMapEvents } from 'react-leaflet'
import {
  useDispatch,
  useSelector
} from 'react-redux'
import { useSearchParams } from 'react-router-dom'
import L from 'leaflet'
import {
  getNotificationMessage,
  markersSelectors,
  setIsUpdatingAvatar,
  updateMarkerAvatars,
  usersSelectors
} from 'store'
import dayjs from 'dayjs'
import { REJECTED } from 'utils/constants'
// eslint-disable-next-line import/no-extraneous-dependencies
import MarkerClusterGroup from '@changey/react-leaflet-markercluster'
import {
  convertToOwnTimeZone,
  isAuth,
  whatMarkerColor
} from 'utils/helpers'
import { v4 as uuid } from 'uuid'

import { CopyEventMarkerItem } from './CopyEventMarkerItem'
import { CopyMarkerItem } from './CopyMarkerItem'
import { EventMarkerItem } from './EventMarkerItem'
import { MarkerItem } from './MarkerItem'

export const Markers = ({
  eventMarkers,
  markers,
  mapRef,
  setShowMarkerCount
}) => {
  const {
    userProfile,
    activeProfile
  } = useSelector(usersSelectors)
  const dispatch = useDispatch()
  const [searchParams, setSearchParams] = useSearchParams()
  const {
    isFiltered,
    filters,
    markerAvatars,
    isUpdatingAvatar
  } = useSelector(markersSelectors)

  const handleOriginalMarker = (
    item,
    profileAvatar,
    isInsideOfCluster
  ) => (
    <MarkerItem
      item={item}
      profileAvatar={profileAvatar}
      isInsideOfCluster={isInsideOfCluster}
    />
  )

  const handleCopyOriginalMarker = (
    item,
    trueLng,
    profileAvatar,
    isInsideOfCluster
  ) => (
    <CopyMarkerItem
      item={{ ...item, coordinates: [item.coordinates[0], trueLng] }}
      avatar={profileAvatar}
      mapRef={mapRef}
      isInsideOfCluster={isInsideOfCluster}
    />
  )

  const mapItem = useMapEvents({
    moveend: () => {
      localStorage.setItem('northeast', JSON.stringify(mapItem?.getBounds().getNorthEast()))
      localStorage.setItem('southwest', JSON.stringify(mapItem?.getBounds().getSouthWest()))

      let northEast = L.latLng(JSON.parse(localStorage.getItem('northeast')))
      let southWest = L.latLng(JSON.parse(localStorage.getItem('southwest')))

      let markerId = []
      markers.forEach((item) => {
        if (markerAvatars.some((e) => e?.markerID === item?.id) === false) {
          const duplicatedLNGofNE = northEast?.lng > 0
            ? northEast?.lng - 360
            : northEast?.lng + 360

          const duplicatedLNGofSW = southWest?.lng > 0
            ? southWest?.lng - 360
            : southWest?.lng + 360

          if (
            item?.is_friends === 'accepted'
            && (
              (
                item.coordinates[0] <= northEast.lat
                && item.coordinates[0] >= southWest.lat
                && item.coordinates[1] <= northEast.lng
                && item.coordinates[1] >= southWest.lng
              )
              || (
                item.coordinates[0] <= northEast.lat
                && item.coordinates[0] >= southWest.lat
                && item.coordinates[1] <= duplicatedLNGofNE
                && item.coordinates[1] >= duplicatedLNGofSW
              )
            )
          ) {
            markerId.push(item?.id)
          }
        }
      })

      if (markerId.length > 0 && isUpdatingAvatar === false) {
        dispatch(setIsUpdatingAvatar(true))
        setTimeout(() => dispatch(updateMarkerAvatars({ isAuth: true, markerId })), 500)
      }
    }
  })

  const generateAllMarkers = (isInsideOfCluster) => {
    const markerList = []
    const currZoom = mapRef?.current?.getZoom()

    markers.forEach((item) => {
      if (
        typeof item?.coordinates[0] === 'number'
        && typeof item?.coordinates[1] === 'number'
      ) {
        if (isAuth()) {
          if (
            currZoom < 12
            && whatMarkerColor(dayjs(item?.last_accessed)) === 'green'
          ) {
            if (isInsideOfCluster) {
              return
            }
          } else if (!isInsideOfCluster) {
            return
          }
        }

        let profileAvatar = null
        const match = markerAvatars?.find((ma) => ma.markerID === item?.id)

        if (match) {
          profileAvatar = match.imageBase64
        }

        markerList.push(handleOriginalMarker(
          item,
          profileAvatar,
          isInsideOfCluster
        ))

        // Duplicate all markers so even if the
        // screen is wide, all the markers will still be visible
        const trueLng = item.coordinates[1] > 0
          ? item.coordinates[1] - 360
          : item.coordinates[1] + 360

        markerList.push(handleCopyOriginalMarker(
          item,
          trueLng,
          profileAvatar,
          isInsideOfCluster
        ))
      }
    })

    return markerList
  }

  // For adding own profile markers since it is not included in the function before this
  const generateOwnMarkers = () => {
    const markerList = []

    userProfile?.forEach((item) => {
      if (
        item?.full_name
        && typeof item?.coordinates[0] === 'number'
        && typeof item?.coordinates[1] === 'number'
      ) {
        let profileAvatar = null

        const match = userProfile?.find((up) => up.markerID === item?.id)
        if (match) {
          profileAvatar = match.imageBase64
        }

        markerList.push(handleOriginalMarker(
          item,
          profileAvatar,
          false
        ))

        const trueLng = item.coordinates[1] > 0
          ? item.coordinates[1] - 360
          : item.coordinates[1] + 360

        markerList.push(handleCopyOriginalMarker(
          item,
          trueLng,
          profileAvatar,
          false
        ))
      }
    })

    return markerList
  }

  const checkAffinities = (affinityList, filterList) => {
    const filterLenth = filterList.length
    let successCount = 0

    if (affinityList) {
      filterList.forEach((filter) => {
        if (affinityList?.includes(filter)) {
          successCount += 1
        }
      })
    }

    if (filterLenth === successCount) {
      return true
    }

    return false
  }

  const checkCodewords = (codewordsList, filterList) => {
    const filterLenth = filterList.length
    let successCount = 0
    filterList.forEach((filter) => {
      if (codewordsList?.includes(filter.toString())) {
        successCount += 1
      }
    })
    if (filterLenth === successCount) {
      return true
    }

    return false
  }

  const generateFilteredMarkers = (filter, isInsideOfCluster) => {
    const markerList = []
    const currZoom = mapRef?.current?.getZoom()

    if (activeProfile?.full_name) {
      markers.forEach((item) => {
        if (
          checkAffinities(item?.affinities, filter?.affinities)
          && checkCodewords(item?.codewords?.map((codeword) => codeword), filter?.codewords)
          && typeof item?.coordinates[0] === 'number'
          && typeof item?.coordinates[1] === 'number'
        ) {
          if (isAuth()) {
            if (
              currZoom < 12
              && whatMarkerColor(dayjs(item?.last_accessed)) === 'green'
            ) {
              if (isInsideOfCluster) {
                return
              }
            } else if (!isInsideOfCluster) {
              return
            }
          }

          let profileAvatar = null
          const match = userProfile?.find((up) => up.markerID === item?.id)
          const match2 = markerAvatars?.find((ma) => ma.markerID === item?.id)

          if (match) {
            profileAvatar = match.imageBase64
          } else if (match2) {
            profileAvatar = match2.imageBase64
          }

          markerList.push(handleOriginalMarker(
            item,
            profileAvatar,
            isInsideOfCluster
          ))

          // Duplicate all markers so even if
          // the screen is wide, all the markers will still be visible
          const trueLng = item.coordinates[1] > 0
            ? item.coordinates[1] - 360
            : item.coordinates[1] + 360

          markerList.push(handleCopyOriginalMarker(
            item,
            trueLng,
            profileAvatar,
            isInsideOfCluster
          ))
        }
      })

      userProfile?.forEach((item) => {
        if (
          item?.full_name
          && checkAffinities(item?.affinities?.map((affi) => affi?.title), filter?.affinities)
          && checkCodewords(item?.code_words?.map((cw) => cw?.title), filter?.codewords)
          && typeof item?.coordinates[0] === 'number'
          && typeof item?.coordinates[1] === 'number'
        ) {
          if (isAuth()) {
            if (
              currZoom < 12
              && whatMarkerColor(dayjs(item?.last_accessed)) === 'green'
            ) {
              if (isInsideOfCluster) {
                return
              }
            } else if (!isInsideOfCluster) {
              return
            }
          }

          let profileAvatar = null
          const match = userProfile?.find((up) => up.markerID === item?.id)

          if (match) {
            profileAvatar = match.imageBase64
          }

          markerList.push(handleOriginalMarker(
            item,
            profileAvatar,
            isInsideOfCluster
          ))

          const trueLng = item.coordinates[1] > 0
            ? item.coordinates[1] - 360
            : item.coordinates[1] + 360

          markerList.push(handleCopyOriginalMarker(
            item,
            trueLng,
            profileAvatar,
            isInsideOfCluster
          ))
        }
      })
    } else {
      dispatch(getNotificationMessage({
        type: REJECTED,
        message: 'Create a profile first before adding a filter.',
        isEditProfile: true
      }))
    }

    return markerList
  }

  const generateEventMarkers = () => {
    const markerList = []

    eventMarkers?.forEach((eventItem) => {
      if (
        (
          convertToOwnTimeZone(eventItem?.end_date, 'newDate') > new Date()
          && activeProfile?.affinities?.find((aff) => eventItem?.affinities?.includes(aff.title))
          && !filters?.affinities?.length
          && !filters?.codewords?.length
        )
        || (
          convertToOwnTimeZone(eventItem?.end_date, 'newDate') > new Date()
          && filters?.affinities?.length
          && filters?.affinities?.every((title) => eventItem?.affinities?.includes(title))
          && !filters?.codewords?.length
        )
      ) {
        markerList.push(<EventMarkerItem eventItem={eventItem} />)

        const trueLng = eventItem.lng > 0
          ? eventItem.lng - 360
          : eventItem.lng + 360

        markerList.push((
          <CopyEventMarkerItem
            eventItem={{ ...eventItem, lng: trueLng }}
            mapRef={mapRef}
            key={uuid()}
          />
        ))
      }
    })

    return markerList
  }

  return (
    <>
      {generateEventMarkers()}
      {isFiltered === true || !isAuth() ? null : generateAllMarkers(false)}
      {isFiltered === true && isAuth() ? generateFilteredMarkers(filters, false) : null}

      <MarkerClusterGroup
        showCoverageOnHover={false}
        animate
        chunkedLoading
        className="marker-cluster-custom"
        spiderfyOnMaxZoom={false}
        disableClusteringAtZoom={14}
        maxClusterRadius={20}
        eventHandlers={{
          animationend: () => {
            if (window.location.search.includes('?id') || window.location.search.includes('?eventid')) {
              const popUpExists = document.querySelector('.leaflet-popup')

              if (!popUpExists) {
                const mapCenter = mapRef.current.getCenter()
                const clickedZoom = mapRef.current.getZoom()

                localStorage.setItem('lat', mapCenter.lat.toFixed(6))
                localStorage.setItem('lng', mapCenter.lng.toFixed(6))
                localStorage.setItem('zoom', clickedZoom)

                setSearchParams({
                  lat: mapCenter.lat.toFixed(6),
                  lng: mapCenter.lng.toFixed(6),
                  zoom: clickedZoom
                })

                sessionStorage.removeItem('id')
                sessionStorage.removeItem('eventid')

                setTimeout(() => {
                  setShowMarkerCount('flex')
                }, 500)
              }
            }
          }
        }}
      >
        {isFiltered === true || !isAuth() ? null : generateOwnMarkers()}
        {isFiltered === true ? null : generateAllMarkers(true)}
        {isFiltered === true && isAuth() ? generateFilteredMarkers(filters, true) : null}
      </MarkerClusterGroup>
    </>
  )
}
